// src/admin/components/services/ServicePaymentList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    RiWifiLine,
    RiFilter2Line,
    RiSearchLine,
    RiEyeLine,
    RiRefreshLine,
    RiCalendarLine,
    RiBuildingLine,
    RiDownloadLine,
    RiFilterOffLine,
    RiArrowRightLine,
    RiArrowLeftLine,
    RiCheckLine,
    RiCloseLine,
    RiPrinterLine
} from 'react-icons/ri';
import {
    getAllServicePayments,
    formatCurrency,
    getMonthName,
    getPaymentStatusColor
} from '../../../models/serviceModel';
import { getAllBuildings } from '../../../models/buildingModel';
import { useTheme } from '../../../contexts/ThemeContext';
import * as XLSX from 'xlsx';
import PrintServiceReceipt from './PrintServiceReceipt';

const ServicePaymentList = () => {
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [buildings, setBuildings] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);

    // Print Receipt State
    const [showReceipt, setShowReceipt] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    // Filters state
    const [filters, setFilters] = useState({
        buildingId: '',
        year: new Date().getFullYear().toString(),
        month: '',
        isPaid: '',
        searchTerm: ''
    });

    // Pagination state
    const [pagination, setPagination] = useState({
        totalCount: 0,
        page: 1,
        limit: 15,
        totalPages: 0
    });

    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    // Fetch buildings for filter
    const fetchBuildings = useCallback(async () => {
        try {
            const data = await getAllBuildings();
            setBuildings(data?.buildings || []);
        } catch (err) {
            console.error('Error fetching buildings:', err);
        }
    }, []);

    // Fetch service payments
    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
                isPaid: filters.isPaid === 'true' ? true : (filters.isPaid === 'false' ? false : undefined)
            };

            const response = await getAllServicePayments(params);

            if (response.success) {
                setPayments(response.payments);
                setPagination(prev => ({
                    ...prev,
                    ...response.pagination
                }));
            } else {
                setError(t('failed_to_load_service_data'));
            }
        } catch (err) {
            console.error('Error fetching service payments:', err);
            setError(t('failed_to_load_service_data'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.page, pagination.limit, t]);

    useEffect(() => {
        fetchBuildings();
    }, [fetchBuildings]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const resetFilters = () => {
        setFilters({
            buildingId: '',
            year: new Date().getFullYear().toString(),
            month: '',
            isPaid: '',
            searchTerm: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };


    const exportToExcel = () => {
        const exportData = payments.map(p => ({
            [t('apartment_number')]: p.apartmentNumber,
            [t('building')]: p.buildingName,
            [t('period')]: `${getMonthName(p.month)} ${p.year}`,
            [t('amount')]: p.amount,
            [t('status')]: p.isPaid ? t('paid') : t('unpaid'),
            [t('tenant_name')]: p.tenantName || '',
            [t('owner_name')]: p.ownerName || '',
            [t('payment_date')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '',
            [t('notes')]: p.notes || ''
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Service Payments');
        XLSX.writeFile(wb, `service_payments_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handlePrint = (payment) => {
        setSelectedPayment(payment);
        setShowReceipt(true);
    };

    return (
        <div className="p-4 md:p-6 text-start" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                        {t('all_service_payments')}
                    </h1>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                        {t('manage_all_service_payments_across_buildings')}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setFilterOpen(!filterOpen)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border
              ${filterOpen
                                ? 'bg-blue-600 text-white border-blue-600'
                                : isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}
                    >
                        <RiFilter2Line size={18} />
                        <span className="hidden sm:inline">{t('filter')}</span>
                    </button>

                    <button
                        onClick={exportToExcel}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border
              ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}
                    >
                        <RiDownloadLine size={18} />
                        <span className="hidden sm:inline">{t('export')}</span>
                    </button>

                    <button
                        onClick={fetchPayments}
                        className={`flex items-center justify-center p-2 rounded-lg transition-colors border
              ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}
                    >
                        <RiRefreshLine size={18} />
                    </button>
                </div>
            </div>

            {/* Search and Filters */}
            <div className={`mb-6 p-4 rounded-xl border transition-all
        ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}
        ${filterOpen ? 'block' : 'hidden md:block'}`}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Search Term */}
                    <div className="lg:col-span-1">
                        <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            {t('search')}
                        </label>
                        <div className="relative">
                            <RiSearchLine className="absolute start-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                            <input
                                type="text"
                                name="searchTerm"
                                value={filters.searchTerm}
                                onChange={handleFilterChange}
                                placeholder={t('search_apartments_tenants')}
                                className={`w-full ps-10 pe-4 py-2 rounded-lg text-sm border focus:ring-2 focus:ring-blue-500 outline-none
                  ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600' : 'bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400'}`}
                            />
                        </div>
                    </div>

                    {/* Building Filter */}
                    <div>
                        <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            {t('building')}
                        </label>
                        <select
                            name="buildingId"
                            value={filters.buildingId}
                            onChange={handleFilterChange}
                            className={`w-full px-3 py-2 rounded-lg text-sm border focus:ring-2 focus:ring-blue-500 outline-none
                ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900'}`}
                        >
                            <option value="">{t('all_buildings')}</option>
                            {buildings.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Year Filter */}
                    <div>
                        <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            {t('year')}
                        </label>
                        <select
                            name="year"
                            value={filters.year}
                            onChange={handleFilterChange}
                            className={`w-full px-3 py-2 rounded-lg text-sm border focus:ring-2 focus:ring-blue-500 outline-none
                ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900'}`}
                        >
                            {[2023, 2024, 2025, 2026, 2027].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Month Filter */}
                    <div>
                        <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            {t('month')}
                        </label>
                        <select
                            name="month"
                            value={filters.month}
                            onChange={handleFilterChange}
                            className={`w-full px-3 py-2 rounded-lg text-sm border focus:ring-2 focus:ring-blue-500 outline-none
                ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900'}`}
                        >
                            <option value="">{t('all_months')}</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                <option key={m} value={m}>{getMonthName(m)}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div className="flex flex-col justify-end">
                        <button
                            onClick={resetFilters}
                            className={`flex items-center justify-center gap-2 px-4 py-2 h-[38px] rounded-lg text-sm transition-colors border
                ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 border-zinc-200 text-zinc-600 hover:bg-zinc-200'}`}
                        >
                            <RiFilterOffLine size={16} />
                            {t('reset')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Table */}
            <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white'}`}>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className={`${isDarkMode ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-500'} text-xs font-medium text-start uppercase tracking-wider`}>
                                <th className="px-6 py-4">{t('apartment')}</th>
                                <th className="px-6 py-4">{t('building')}</th>
                                <th className="px-6 py-4">{t('period')}</th>
                                <th className="px-6 py-4">{t('amount')}</th>
                                <th className="px-6 py-4">{t('status')}</th>
                                <th className="px-6 py-4">{t('payer')}</th>
                                <th className="px-6 py-4 text-center">{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-zinc-100'}`}>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="7" className="px-6 py-8">
                                            <div className={`h-6 rounded w-3/4 mx-auto ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}></div>
                                        </td>
                                    </tr>
                                ))
                            ) : payments.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className={`p-4 rounded-full mb-4 ${isDarkMode ? 'bg-zinc-800 text-zinc-600' : 'bg-zinc-50 text-zinc-400'}`}>
                                                <RiWifiLine size={48} />
                                            </div>
                                            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-900'}`}>
                                                {t('no_payments_found')}
                                            </h3>
                                            <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                                {t('no_matching_payments_found')}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                payments.map((p) => (
                                    <tr key={p.id} className={`${isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-50/50'} transition-colors`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                                    {p.apartmentNumber}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <RiBuildingLine className="text-zinc-400" size={16} />
                                                <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                                    {p.buildingName}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <RiCalendarLine className="text-zinc-400" size={16} />
                                                <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                                    {getMonthName(p.month)} {p.year}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {formatCurrency(p.amount)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold
                        ${getPaymentStatusColor(p.isPaid, isDarkMode)}`}>
                                                {p.isPaid ? <RiCheckLine size={14} /> : <RiCloseLine size={14} />}
                                                {p.isPaid ? t('paid') : t('unpaid')}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                                    {p.tenantName || p.ownerName || '-'}
                                                </span>
                                                <span className={`text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                                    {p.tenantName ? t('tenant') : (p.ownerName ? t('owner') : '')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <Link
                                                    to={`/admin/services/detail/${p.id}`}
                                                    className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors
                              ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'}`}
                                                    title={t('view_details')}
                                                >
                                                    <RiEyeLine size={18} />
                                                </Link>

                                                {p.isPaid && (
                                                    <button
                                                        onClick={() => handlePrint(p)}
                                                        className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors
                                                            ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'}`}
                                                        title={t('print_receipt')}
                                                    >
                                                        <RiPrinterLine size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <div className={`px-6 py-4 flex items-center justify-between border-t ${isDarkMode ? 'border-zinc-800' : 'border-zinc-100'}`}>
                        <div className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            {t('showing_range', {
                                startItem: (pagination.page - 1) * pagination.limit + 1,
                                endItem: Math.min(pagination.page * pagination.limit, pagination.totalCount),
                                totalItems: pagination.totalCount
                            })}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                                className={`p-2 rounded-lg transition-colors disabled:opacity-30
                  ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                            >
                                {isRTL ? <RiArrowRightLine size={18} /> : <RiArrowLeftLine size={18} />}
                            </button>

                            <div className="flex items-center gap-1">
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - pagination.page) <= 1)
                                    .map((p, i, arr) => (
                                        <React.Fragment key={p}>
                                            {i > 0 && arr[i - 1] !== p - 1 && <span className="px-2 text-zinc-500">...</span>}
                                            <button
                                                onClick={() => handlePageChange(p)}
                                                className={`w-8 h-8 rounded-lg text-sm transition-colors
                          ${pagination.page === p
                                                        ? 'bg-blue-600 text-white'
                                                        : isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                                            >
                                                {p}
                                            </button>
                                        </React.Fragment>
                                    ))
                                }
                            </div>

                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages}
                                className={`p-2 rounded-lg transition-colors disabled:opacity-30
                  ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                            >
                                {isRTL ? <RiArrowLeftLine size={18} /> : <RiArrowRightLine size={18} />}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Print Modal */}
            {
                showReceipt && selectedPayment && (
                    <PrintServiceReceipt
                        payment={selectedPayment}
                        onClose={() => setShowReceipt(false)}
                    />
                )
            }
        </div >
    );
};

export default ServicePaymentList;
